/**
 * KanbanCard.tsx - KanbanCard - Card component for Kanban board
 * 
 * CHANGE HISTORY:
 * - 251021_0421 [7529d5e] danielbeer: feat: migrate all packages to TypeScript structure
 * - 251108_0955 [current] danielbeer: feat: Add context menu support
 */
import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const KanbanCard = ({ item, getField, onClick, onContextMenu, cardStyles = {}, columnId }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: item.id,
    data: {
      type: 'card',
      columnId: columnId, // The column this card belongs to
      item: item
    }
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    ...cardStyles,
  };

  // Get card fields with proper fallbacks
  const cardTitle = getField(item, 'titleField') || item.title || item.name || item.content || 'Untitled';
  const cardDescription = getField(item, 'descriptionField') || item.description || '';
  const cardPriority = getField(item, 'priorityField') || item.priority || 'medium';
  const cardTags = getField(item, 'tagsField') || item.tags || [];
  const cardAssignee = getField(item, 'assigneeField') || item.assignee || '';

  // Priority colors
  const priorityColors = {
    low: '#10b981',
    medium: '#f59e0b',
    high: '#ef4444',
    critical: '#dc2626'
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`kanban-card ${isDragging ? 'dragging' : ''}`}
      {...attributes}
      {...listeners}
      onClick={onClick}
      onContextMenu={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (onContextMenu) {
          onContextMenu(e, item);
        }
      }}
    >
      <div className="kanban-card-header">
        <div className="kanban-card-title">{cardTitle}</div>
        {cardPriority && (
          <div 
            className="kanban-card-priority"
            style={{ 
              width: '8px', 
              height: '8px', 
              borderRadius: '50%',
              backgroundColor: priorityColors[cardPriority] || priorityColors.medium,
              flexShrink: 0
            }}
            title={`Priority: ${cardPriority}`}
          />
        )}
      </div>
      
      {cardDescription && (
        <div className="kanban-card-description">
          {cardDescription.length > 100 ? cardDescription.substring(0, 100) + '...' : cardDescription}
        </div>
      )}
      
      {(cardTags.length > 0 || cardAssignee) && (
        <div className="kanban-card-footer">
          {cardTags.length > 0 && (
            <div className="kanban-card-tags">
              {cardTags.slice(0, 3).map((tag, idx) => (
                <span key={idx} className="kanban-card-tag">{tag}</span>
              ))}
              {cardTags.length > 3 && (
                <span className="kanban-card-tag">+{cardTags.length - 3}</span>
              )}
            </div>
          )}
          {cardAssignee && (
            <div className="kanban-card-meta" title={cardAssignee}>
              👤 {cardAssignee.split(' ')[0]}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default KanbanCard;
